// src/ui/components/passageCard.ts — KS2 Reading Comprehension Passage Card
// Implements FR-ENG-001, FR-ENG-002, NFR-STYLE-001, NFR-TOUCH-001, CON-004.
// Strict constraints:
// - Max 60 words per card (ADHD cognitive load reduction).
// - Font size >= 18px for neurodivergent reading comfort.
// - Tablet swipe gestures + desktop pagination buttons (touch targets >= 48px).
// - Tap-to-highlight sentence interaction with bioluminescent styling.

import { tokens } from '../designTokens';
import { renderCornerFiligree } from './ornateHelpers';

export interface VocabularyQuestion {
  targetWord?: string;
  question: string;
  options: string[];
  answerIndex: number;
  explanation: string;
}

export interface PassageData {
  id: string;
  title: string;
  domain: '2a' | '2b' | '2d' | '2e' | '2g';
  domainLabel: string;
  text: string;
  sentences: string[];
  illustration?: string;
  targetWord?: string;
  inferenceClaim?: string;
  correctSentenceIndex?: number;
  socraticHint?: string;
  vocabularyQuestion?: VocabularyQuestion;
  cardImage?: string;
  cardSubtitle?: string;
  cardGlowColor?: string;
}

export interface PassageCardOptions {
  passages: PassageData[];
  initialIndex?: number;
  onSentenceClick?: (sentenceIndex: number, sentenceText: string, passage: PassageData) => void;
  onPassageChange?: (newIndex: number, passage: PassageData) => void;
}

export const MAX_PASSAGE_WORDS = 60;
export const MIN_FONT_SIZE_PX = 18;

/** Count words cleanly using whitespace delimiters */
export function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Split a text exceeding max words into multiple parts <= max words */
export function splitPassageIntoChunks(text: string, maxWords: number = MAX_PASSAGE_WORDS): string[] {
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return [text];

  const chunks: string[] = [];
  let currentChunk: string[] = [];

  for (const word of words) {
    if (currentChunk.length >= maxWords) {
      chunks.push(currentChunk.join(' '));
      currentChunk = [];
    }
    currentChunk.push(word);
  }

  if (currentChunk.length > 0) {
    chunks.push(currentChunk.join(' '));
  }

  return chunks;
}

export class PassageCard {
  private passages: PassageData[];
  private currentIndex: number;
  private selectedSentenceIndex: number | null = null;
  private onSentenceClick?: (sentenceIndex: number, sentenceText: string, passage: PassageData) => void;
  private onPassageChange?: (newIndex: number, passage: PassageData) => void;
  private container: HTMLElement | null = null;

  // Touch swipe tracking for Samsung Galaxy Tab A9+
  private touchStartX = 0;
  private touchStartY = 0;

  constructor(options: PassageCardOptions) {
    if (!options.passages || options.passages.length === 0) {
      throw new Error('PassageCard requires at least one passage.');
    }
    this.passages = options.passages;
    this.currentIndex = Math.max(0, Math.min(options.initialIndex ?? 0, this.passages.length - 1));
    this.onSentenceClick = options.onSentenceClick;
    this.onPassageChange = options.onPassageChange;
  }

  /** Current active passage data */
  get currentPassage(): PassageData {
    return this.passages[this.currentIndex]!;
  }

  /** Current card index (0-based) */
  get activeIndex(): number {
    return this.currentIndex;
  }

  /** Total passages count */
  get totalCount(): number {
    return this.passages.length;
  }

  /** Currently selected/highlighted sentence index */
  get highlightedSentenceIndex(): number | null {
    return this.selectedSentenceIndex;
  }

  /** Set highlighted sentence index programmatically */
  setHighlightedSentence(index: number | null): void {
    this.selectedSentenceIndex = index;
    if (this.container) {
      const sentenceEls = this.container.querySelectorAll<HTMLElement>('.passage-sentence');
      sentenceEls.forEach((el, idx) => {
        const isHighlighted = idx === index;
        el.setAttribute('aria-pressed', isHighlighted ? 'true' : 'false');
        if (isHighlighted) {
          el.classList.add('sentence-highlighted');
          el.style.backgroundColor = 'rgba(0, 229, 255, 0.18)';
          el.style.borderColor = tokens.color.biolumCyan;
          el.style.boxShadow = '0 0 12px rgba(0, 229, 255, 0.35)';
        } else {
          el.classList.remove('sentence-highlighted');
          el.style.backgroundColor = 'transparent';
          el.style.borderColor = 'transparent';
          el.style.boxShadow = 'none';
        }
      });
    }
  }

  /** Navigate to specific passage */
  goTo(index: number): void {
    if (index >= 0 && index < this.passages.length && index !== this.currentIndex) {
      this.currentIndex = index;
      this.selectedSentenceIndex = null;
      if (this.onPassageChange) {
        this.onPassageChange(this.currentIndex, this.currentPassage);
      }
      this.updateView();
    }
  }

  /** Navigate to next passage */
  next(): void {
    if (this.currentIndex < this.passages.length - 1) {
      this.goTo(this.currentIndex + 1);
    }
  }

  /** Navigate to previous passage */
  previous(): void {
    if (this.currentIndex > 0) {
      this.goTo(this.currentIndex - 1);
    }
  }

  /** Render the passage card DOM element */
  render(): HTMLElement {
    const cardWrapper = document.createElement('article');
    cardWrapper.className = 'passage-card-container';
    cardWrapper.id = 'passage-card-viewer';
    cardWrapper.style.width = '100%';
    cardWrapper.style.maxWidth = '840px';
    cardWrapper.style.margin = '0 auto';

    this.container = cardWrapper;
    this.updateView();
    this.attachSwipeHandlers(cardWrapper);

    return cardWrapper;
  }

  /** Update DOM contents when passage changes */
  private updateView(): void {
    if (!this.container) return;

    const passage = this.currentPassage;
    const wordCount = countWords(passage.text);
    const isExceedingLimit = wordCount > MAX_PASSAGE_WORDS;

    // Strict validation warning / auto-chunk display
    let displayText = passage.text;
    if (isExceedingLimit) {
      console.warn(`[PassageCard] Passage "${passage.id}" exceeds limit (${wordCount} words > ${MAX_PASSAGE_WORDS} max).`);
      const chunks = splitPassageIntoChunks(passage.text, MAX_PASSAGE_WORDS);
      displayText = chunks[0] ?? passage.text;
    }

    const sentences = passage.sentences && passage.sentences.length > 0
      ? passage.sentences
      : displayText.split(/(?<=[.?!])\s+/).filter(Boolean);

    this.container.innerHTML = `
      <div 
        class="vellum-surface morris-card frosted-glass" 
        style="
          padding: 28px 24px;
          border: 2px solid ${tokens.color.gildedBrass};
          border-radius: 16px;
          position: relative;
        "
      >
        ${renderCornerFiligree()}
        <!-- Header meta bar: Domain, Word count & Card pagination index -->
        <header style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 20px; border-bottom: 1px solid rgba(229, 169, 60, 0.25); padding-bottom: 12px;">
          <div style="display: flex; align-items: center; gap: 10px;">
            ${passage.cardImage ? `
              <img 
                src="${passage.cardImage}" 
                alt="${passage.title}" 
                style="
                  width: 32px; 
                  height: 42px; 
                  object-fit: cover; 
                  border-radius: 5px; 
                  border: 1.5px solid ${passage.cardGlowColor || tokens.color.gildedBrass};
                  box-shadow: 0 0 8px ${passage.cardGlowColor || 'rgba(0, 229, 255, 0.3)'};
                  flex-shrink: 0;
                "
              />
            ` : `<span style="font-size: 1.6rem;" role="img" aria-label="Illustration">${passage.illustration || '🦞'}</span>`}
            <span style="
              font-size: 0.8rem;
              font-weight: 800;
              text-transform: uppercase;
              padding: 4px 10px;
              border-radius: 6px;
              background: rgba(26, 54, 93, 0.08);
              color: ${tokens.color.morrisIndigo};
              border: 1px solid rgba(26, 54, 93, 0.15);
            ">
              ${passage.domainLabel || `Domain ${passage.domain.toUpperCase()}`}
            </span>
            ${passage.cardSubtitle ? `
              <span style="
                font-size: 0.8rem;
                font-weight: 800;
                padding: 4px 10px;
                border-radius: 6px;
                background: rgba(255, 253, 249, 0.9);
                color: ${tokens.color.morrisIndigo};
                border: 1px solid ${passage.cardGlowColor || 'rgba(26, 54, 93, 0.2)'};
              ">
                Field Card #${this.currentIndex + 1} · ${passage.cardSubtitle}
              </span>
            ` : ''}
          </div>

          <div style="display: flex; align-items: center; gap: 12px;">
            <span 
              id="passage-word-count-badge" 
              style="
                font-family: ${tokens.font.mono};
                font-size: 0.8rem;
                font-weight: 700;
                color: ${isExceedingLimit ? tokens.color.madderCoral : tokens.color.seaweedSage};
                background: ${isExceedingLimit ? 'rgba(255, 107, 74, 0.1)' : 'rgba(61, 90, 69, 0.1)'};
                padding: 4px 8px;
                border-radius: 6px;
              "
            >
              ${wordCount} words ${wordCount <= MAX_PASSAGE_WORDS ? '✓ ≤60' : '⚠️ >60'}
            </span>
            <span 
              id="passage-index-badge"
              style="
                font-family: ${tokens.font.mono};
                font-size: 0.85rem;
                font-weight: 800;
                color: ${tokens.color.antiqueBrass};
              "
            >
              ${this.currentIndex + 1} / ${this.passages.length}
            </span>
          </div>
        </header>

        <!-- Titleplate -->
        <h3 
          id="passage-title" 
          style="
            font-family: ${tokens.font.heading};
            font-size: 1.7rem;
            color: ${tokens.color.morrisIndigo};
            margin-bottom: 16px;
            letter-spacing: 0.02em;
          "
        >
          ${passage.title}
        </h3>

        <!-- Interactive passage text body (font-size >= 18px strictly enforced) -->
        <div 
          id="passage-body-text"
          class="passage-body"
          style="
            font-family: ${tokens.font.body};
            font-size: ${MIN_FONT_SIZE_PX}px;
            line-height: 1.75;
            color: ${tokens.color.morrisIndigo};
            margin-bottom: 24px;
            background: rgba(255, 253, 249, 0.7);
            padding: 20px;
            border-radius: 12px;
            border: 1px solid rgba(26, 54, 93, 0.08);
            overflow: hidden;
          "
        >
          <div style="
            float: right;
            width: 140px;
            max-width: 32%;
            margin: 0 0 14px 18px;
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 0 16px ${passage.cardGlowColor || 'rgba(0, 229, 255, 0.35)'}, 0 4px 12px rgba(10, 25, 47, 0.2);
            border: 2px solid ${passage.cardGlowColor || tokens.color.gildedBrass};
          ">
            <img 
              src="${passage.cardImage || '/assets/cards/card-1-lobster.png'}" 
              alt="${passage.title}" 
              style="width: 100%; height: auto; display: block;" 
            />
          </div>
          ${sentences.map((s, idx) => {
      const isSelected = this.selectedSentenceIndex === idx;
      return `
              <span
                class="passage-sentence touch-target-text ${isSelected ? 'sentence-highlighted' : ''}"
                data-sentence-index="${idx}"
                role="button"
                tabindex="0"
                aria-pressed="${isSelected ? 'true' : 'false'}"
                style="
                  display: inline;
                  cursor: pointer;
                  padding: 3px 6px;
                  margin: 0 1px;
                  border-radius: 6px;
                  border: 1px solid ${isSelected ? tokens.color.biolumCyan : 'transparent'};
                  background-color: ${isSelected ? 'rgba(0, 229, 255, 0.18)' : 'transparent'};
                  box-shadow: ${isSelected ? '0 0 12px rgba(0, 229, 255, 0.35)' : 'none'};
                  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
                "
              >${s}</span>
            `;
    }).join(' ')}
        </div>

        <!-- Desktop & Tablet Pagination Navigation (>=48px touch targets) -->
        <footer style="display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 8px;">
          <button 
            id="btn-prev-passage" 
            class="touch-target btn-secondary"
            ${this.currentIndex === 0 ? 'disabled style="opacity: 0.4; cursor: not-allowed;"' : ''}
            style="min-width: 120px; min-height: 48px; font-weight: 800;"
            aria-label="Previous Passage"
          >
            ← Previous
          </button>

          <div style="font-size: 0.85rem; color: #6B7C93; text-align: center;">
            <span class="desktop-only">Use buttons or keys</span>
            <span class="tablet-only">Swipe left / right on tablet</span>
          </div>

          <button 
            id="btn-next-passage" 
            class="touch-target btn-gold"
            ${this.currentIndex === this.passages.length - 1 ? 'disabled style="opacity: 0.4; cursor: not-allowed;"' : ''}
            style="min-width: 120px; min-height: 48px; font-weight: 800;"
            aria-label="Next Passage"
          >
            Next →
          </button>
        </footer>
      </div>
    `;

    this.attachEventListeners();
  }

  /** Attach sentence tap and navigation event listeners */
  private attachEventListeners(): void {
    if (!this.container) return;

    // Previous / Next buttons
    const prevBtn = this.container.querySelector<HTMLButtonElement>('#btn-prev-passage');
    const nextBtn = this.container.querySelector<HTMLButtonElement>('#btn-next-passage');

    prevBtn?.addEventListener('click', () => this.previous());
    nextBtn?.addEventListener('click', () => this.next());

    // Sentence tapping
    const sentenceEls = this.container.querySelectorAll<HTMLElement>('.passage-sentence');
    sentenceEls.forEach(el => {
      const idx = parseInt(el.dataset.sentenceIndex ?? '-1', 10);
      if (idx >= 0) {
        const handler = () => {
          const isCurrentlySelected = this.selectedSentenceIndex === idx;
          this.setHighlightedSentence(isCurrentlySelected ? null : idx);
          if (this.onSentenceClick && !isCurrentlySelected) {
            const sentenceText = el.textContent?.trim() ?? '';
            this.onSentenceClick(idx, sentenceText, this.currentPassage);
          }
        };

        el.addEventListener('click', handler);
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler();
          }
        });
      }
    });
  }

  /** Touch swipe navigation handler for Samsung Galaxy Tab A9+ */
  private attachSwipeHandlers(element: HTMLElement): void {
    element.addEventListener('touchstart', (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      if (touch) {
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
      }
    }, { passive: true });

    element.addEventListener('touchend', (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;

      // Ensure horizontal swipe (deltaX > 50px and larger than vertical deltaY)
      if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
        if (deltaX < 0) {
          this.next(); // swipe left -> next
        } else {
          this.previous(); // swipe right -> previous
        }
      }
    }, { passive: true });
  }
}
